'use client';

import { useTranslation } from 'next-i18next';
import { usePathname } from 'next/navigation';

export interface CurrentMission {
  key: string;
  label: string;
}

const stripLocale = (pathname: string, locale: Locale): string => {
  const prefix = `/${locale}`;
  if (pathname === prefix) return '/';
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  return pathname;
};

/**
 * Maps the current URL to the active mission (for the chip + the "selected" state in the
 * menu). Purely path-based — NOT derived from the prompt-bar mode, because the company
 * context forces chat mode yet a company can still open the candidate job search at
 * `/mc/search`, which must resolve to the `jobs` mission, not `coaching`.
 */
export const useCurrentMission = (locale: Locale): CurrentMission => {
  const { t } = useTranslation('common');
  const pathname = usePathname() ?? '/';
  const path = stripLocale(pathname.split('?')[0], locale).replace(/\/$/, '') || '/';

  if (
    path === '/profile' ||
    path.startsWith('/profile/') ||
    path === '/mc/profile' ||
    path.startsWith('/mc/profile/') ||
    path === '/mc/cv' ||
    path.startsWith('/mc/cv/') ||
    // Company profile editing is part of the unified profile editor.
    path === '/mc/company/edit' ||
    path.startsWith('/mc/company')
  ) {
    return { key: 'edit-profile', label: t('header.label.edit_profile') };
  }
  if (path === '/stories' || path.startsWith('/stories/')) {
    return { key: 'interviews', label: t('mc.label.interviews') };
  }
  if (path === '/mc/job-add/edit' || path.startsWith('/mc/job-add')) {
    return { key: 'add-job', label: t('homepage.react.button.addjob') };
  }
  if (path === '/mc/my-jobs' || path.startsWith('/mc/my-jobs')) {
    return { key: 'candidate-management', label: t('mc.candidate_management') };
  }
  if (path === '/mc/bestie-chat' || path.startsWith('/mc/bestie-chat')) {
    return { key: 'coaching', label: t('mc.career.coaching') };
  }

  // /mc, /mc/search, candidate job lists, and any other MC path → job search.
  return { key: 'jobs', label: t('mc.search.job') };
};
